import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Table, Accordion, Badge } from 'react-bootstrap';
import { FaPlus, FaSave, FaSyringe } from 'react-icons/fa';
import {
  SECTIONS,
  VACCINES,
  VACCINATION_CARD_ITEM_NAME,
  DETAILS_SCHEMA_VERSION,
  TB_TREATMENT_REGIMEN_OPTIONS,
  TB_IGRA_RESULT_OPTIONS,
  TB_PPD_RESULT_OPTIONS,
  buildEmptyVaccinesState,
  buildEmptyTbScreeningState,
  parseVaccinationCardDetailsToState
} from '../constants/vaccinationCardCatalog';
import {
  sanitizeDoseNotesForDisplay,
  doseHasPromotionEvidence,
  openVaccinationCardPromotionEvidence
} from '../utils/vaccinationCardDoseHelpers';

function vaccineStatus(count) {
  if (count === 0) return { label: 'None recorded', variant: 'secondary' };
  if (count === 1) return { label: '1 dose', variant: 'info' };
  return { label: `${count} doses`, variant: 'success' };
}

const DEFAULT_OPEN_SECTION = SECTIONS[0]?.id || 'core';

export default function VaccinationCardModal({ show, onHide, initialDetails, onSubmit, submitting }) {
  const [vaccinesState, setVaccinesState] = useState(buildEmptyVaccinesState);
  const [tbScreening, setTbScreening] = useState(buildEmptyTbScreeningState);
  const [tbXrayFile, setTbXrayFile] = useState(null);
  const [vaccineEvidenceEntries, setVaccineEvidenceEntries] = useState([]);
  const [doseModal, setDoseModal] = useState({ open: false, vaccineId: null, mode: 'add', doseIndex: null });
  const [doseForm, setDoseForm] = useState({ date: '', notes: '' });
  const [openSectionKey, setOpenSectionKey] = useState(DEFAULT_OPEN_SECTION);

  useEffect(() => {
    if (show) {
      const parsed = parseVaccinationCardDetailsToState(initialDetails);
      const { tbScreening: parsedTb, ...parsedVaccines } = parsed;
      setVaccinesState(parsedVaccines);
      setTbScreening(parsedTb || buildEmptyTbScreeningState());
      setTbXrayFile(null);
      try {
        const raw = typeof initialDetails === 'string' ? JSON.parse(initialDetails) : initialDetails || {};
        const incoming = Array.isArray(raw.vaccineEvidenceFiles) ? raw.vaccineEvidenceFiles : [];
        setVaccineEvidenceEntries(
          incoming.map((row, idx) => ({
            id: `existing-${idx}`,
            file: null,
            originalFileName: row?.originalFileName ? String(row.originalFileName) : '',
            path: row?.path ? String(row.path) : '',
            vaccineIds: Array.isArray(row?.vaccineIds) ? row.vaccineIds.filter(Boolean) : []
          }))
        );
      } catch {
        setVaccineEvidenceEntries([]);
      }
      setOpenSectionKey(DEFAULT_OPEN_SECTION);
    }
  }, [show, initialDetails]);

  const totalDosesRecorded = useMemo(() => {
    return VACCINES.reduce((sum, v) => sum + (vaccinesState[v.id]?.doses || []).length, 0);
  }, [vaccinesState]);

  const openAddDose = (vaccineId) => {
    setDoseForm({ date: '', notes: '' });
    setDoseModal({ open: true, vaccineId, mode: 'add', doseIndex: null });
  };

  const saveDose = () => {
    if (!doseModal.vaccineId || !doseForm.date) return;
    const vid = doseModal.vaccineId;
    setVaccinesState((prev) => {
      const cur = prev[vid]?.doses || [];
      if (doseModal.mode === 'edit' && doseModal.doseIndex != null) {
        const nextDoses = cur.map((d, idx) => {
          if (idx !== doseModal.doseIndex) return d;
          const base = { date: doseForm.date, notes: doseForm.notes.trim() };
          if (d.promotionEvidenceId != null) {
            base.promotionEvidenceId = d.promotionEvidenceId;
            if (d.promotionEvidencePath) base.promotionEvidencePath = d.promotionEvidencePath;
            if (d.promotionOriginalFileName) base.promotionOriginalFileName = d.promotionOriginalFileName;
            if (d.promotionAdministrationLocation) {
              base.promotionAdministrationLocation = d.promotionAdministrationLocation;
            }
          }
          return base;
        });
        return { ...prev, [vid]: { doses: nextDoses } };
      }
      return {
        ...prev,
        [vid]: {
          doses: [...cur, { date: doseForm.date, notes: doseForm.notes.trim() }]
        }
      };
    });
    setDoseModal({ open: false, vaccineId: null, mode: 'add', doseIndex: null });
  };

  const handleSubmit = () => {
    const vaccines = {};
    VACCINES.forEach((v) => {
      const entry = vaccinesState[v.id] || {};
      vaccines[v.id] = {
        doses: (entry.doses || []).map((d) => {
          const o = { date: d.date, notes: d.notes != null ? String(d.notes) : '' };
          if (d.promotionEvidenceId != null) {
            o.promotionEvidenceId = Number(d.promotionEvidenceId);
            if (d.promotionEvidencePath) o.promotionEvidencePath = String(d.promotionEvidencePath);
            if (d.promotionOriginalFileName) o.promotionOriginalFileName = String(d.promotionOriginalFileName);
            if (d.promotionAdministrationLocation) {
              o.promotionAdministrationLocation = String(d.promotionAdministrationLocation);
            }
          }
          return o;
        })
      };
    });
    const nextTb = {
      ...tbScreening
    };
    if (!nextTb.diagnosedTb) {
      nextTb.treatedTb = '';
      nextTb.treatmentRegimen = '';
      nextTb.treatmentRegimenOther = '';
    }
    if (nextTb.treatmentRegimen !== 'other') {
      nextTb.treatmentRegimenOther = '';
    }
    if (!nextTb.bcgReceived) {
      nextTb.bcgReceivedDate = '';
      nextTb.igraDone = '';
      nextTb.igraResult = '';
    }
    if (nextTb.ppdDone !== 'yes') {
      nextTb.ppdResult = '';
    }
    if (nextTb.chestXrayDone !== 'yes') {
      nextTb.chestXrayEvidencePath = '';
      nextTb.chestXrayEvidenceOriginalFileName = '';
    }
    onSubmit({
      schemaVersion: DETAILS_SCHEMA_VERSION,
      vaccines,
      vaccineEvidenceFiles: vaccineEvidenceEntries
        .map((entry) => ({
          originalFileName: entry.originalFileName || (entry.file ? entry.file.name : ''),
          path: entry.path || '',
          vaccineIds: Array.isArray(entry.vaccineIds) ? entry.vaccineIds : []
        }))
        .filter((entry) => entry.path && entry.originalFileName && entry.vaccineIds.length > 0),
      tbScreening: nextTb,
      submittedAt: new Date().toISOString()
    }, {
      tbXrayFile,
      vaccineEvidenceEntries
    });
  };

  const sectionVaccines = (sectionId) => VACCINES.filter((v) => v.sectionId === sectionId);

  const formatDoseDate = (isoOrYmd) => {
    if (!isoOrYmd) return '—';
    const s = String(isoOrYmd).trim();
    if (!s) return '—';
    const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const openEditDose = (vaccineId, doseIndex) => {
    const existing = vaccinesState[vaccineId]?.doses?.[doseIndex];
    setDoseForm({
      date: existing?.date || '',
      notes: existing?.notes != null ? String(existing.notes) : ''
    });
    setDoseModal({ open: true, vaccineId, mode: 'edit', doseIndex });
  };

  const removeDose = (vaccineId, doseIndex) => {
    if (!window.confirm('Remove this dose record?')) return;
    setVaccinesState((prev) => {
      const cur = prev[vaccineId]?.doses || [];
      const nextDoses = cur.filter((_, idx) => idx !== doseIndex);
      return {
        ...prev,
        [vaccineId]: { doses: nextDoses }
      };
    });
  };

  const updateTbField = (field, value) => {
    setTbScreening((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'diagnosedTb' && value !== 'yes') {
        next.treatedTb = '';
        next.treatmentRegimen = '';
        next.treatmentRegimenOther = '';
      }
      if (field === 'treatedTb' && value !== 'yes') {
        next.treatmentRegimen = '';
        next.treatmentRegimenOther = '';
      }
      if (field === 'treatmentRegimen' && value !== 'other') {
        next.treatmentRegimenOther = '';
      }
      if (field === 'bcgReceived' && value !== 'yes') {
        next.bcgReceivedDate = '';
        next.igraDone = '';
        next.igraResult = '';
      }
      if (field === 'igraDone') {
        if (value !== 'yes') {
          next.igraResult = '';
        }
      }
      if (field === 'ppdDone' && value !== 'yes') {
        next.ppdResult = '';
      }
      if (field === 'chestXrayDone' && value !== 'yes') {
        next.chestXrayEvidencePath = '';
        next.chestXrayEvidenceOriginalFileName = '';
        setTbXrayFile(null);
      }
      return next;
    });
  };

  const addVaccineEvidenceEntry = () => {
    setVaccineEvidenceEntries((prev) => [
      ...prev,
      { id: `new-${Date.now()}-${prev.length}`, file: null, originalFileName: '', path: '', vaccineIds: [] }
    ]);
  };

  const removeVaccineEvidenceEntry = (id) => {
    setVaccineEvidenceEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateVaccineEvidenceEntry = (id, patch) => {
    setVaccineEvidenceEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
    );
  };

  return (
    <>
      <style>{`
        .modal-dialog.vaccination-card-main-modal-muted {
          opacity: .9;
          filter: grayscale(.6);
          pointer-events: none;
          transition: opacity 0.15s ease, filter 0.15s ease;
        }
      `}</style>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        scrollable
        dialogClassName={doseModal.open ? 'vaccination-card-main-modal-muted' : undefined}
      >
        <Modal.Header closeButton className="border-bottom-0 d-flex flex-row align-items-center gap-2">
          
            <span className="d-inline-flex align-items-center justify-content-center rounded-2 bg-primary text-white p-2">
              <FaSyringe size={18} />
            </span>
            <div className="d-flex flex-column">
              <h6 className="fw-semibold mb-0">Vaccination Completion Card</h6>
              <p className="small text-muted mb-0">{VACCINATION_CARD_ITEM_NAME}</p>
            </div>

        </Modal.Header>
        <Modal.Body className="pt-0">
          {/*<div className="mb-3 p-3 rounded border bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted text-uppercase">Total doses recorded</span>
              <span className="fw-semibold fs-5">{totalDosesRecorded}</span>
            </div>
          </div>*/}

          <Accordion
            activeKey={openSectionKey}
            onSelect={(key) => {
              if (key == null || key === '') {
                return;
              }
              setOpenSectionKey(key);
            }}
          >
            {SECTIONS.map((sec) => (
              <Accordion.Item eventKey={sec.id} key={sec.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center gap-2 w-100 me-2">
                    <span
                      className="rounded-2 flex-shrink-0"
                      style={{
                        width: 8,
                        minHeight: 40,
                        backgroundColor:
                          sec.id === 'core' ? '#0d6efd' : sec.id === 'earlyChildhood' ? '#6f42c1' : '#198754'
                      }}
                    />
                    <div>
                      <div className="fw-semibold">{sec.title}</div>
                      <div className="small text-muted">{sec.subtitle}</div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="pt-0">
                  <Table responsive borderless size="sm" className="mb-0 align-middle">
                    <thead className="text-muted text-uppercase small">
                      <tr>
                        <th>Vaccine</th>
                        <th>Status</th>
                        <th className="text-center">Doses recorded</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionVaccines(sec.id).map((v) => {
                        const doses = vaccinesState[v.id]?.doses || [];
                        const n = doses.length;
                        const st = vaccineStatus(n);
                        return (
                          <React.Fragment key={v.id}>
                            <tr className="border-top">
                              <td style={{ minWidth: 220 }}>
                                <div className="fw-semibold">{v.name}</div>
                                <div className="small text-muted">{v.description}</div>
                              </td>
                              <td>
                                <Badge bg={st.variant}>{st.label}</Badge>
                              </td>
                              <td className="text-center text-nowrap small fw-semibold">{n}</td>
                              <td className="text-end">
                                <Button size="sm" variant="primary" onClick={() => openAddDose(v.id)}>
                                  <FaPlus className="me-1" />
                                  Add dose
                                </Button>
                              </td>
                            </tr>
                            {n > 0 && (
                              <tr>
                                <td colSpan={4} className="px-0">
                                  <div className="px-3 py-3">
                                    <div className="small text-muted text-uppercase mb-2">Dose records</div>
                                    <div className="d-flex flex-column gap-2">
                                      {doses.map((d, idx) => (
                                        <div
                                          key={`${v.id}-dose-${idx}`}
                                          className="d-flex align-items-stretch align-items-md-center gap-2 gap-md-3 bg-white border rounded-3 p-2 flex-wrap flex-md-nowrap"
                                        >
                                          <span
                                            className="d-inline-flex align-items-center justify-content-center rounded-2 bg-secondary text-white flex-shrink-0"
                                            style={{ width: 32, height: 32 }}
                                          >
                                            {idx + 1}
                                          </span>
                                          <div className="flex-grow-1 d-flex flex-column flex-sm-row align-items-sm-start gap-2 small min-w-0">
                                            <span className="text-nowrap">
                                              <b>Date received:</b> {formatDoseDate(d.date)}
                                            </span>
                                            <span className="text-muted min-w-0" style={{ whiteSpace: 'pre-wrap' }}>
                                              <b>Notes:</b>{' '}
                                              {sanitizeDoseNotesForDisplay(d.notes) ? (
                                                sanitizeDoseNotesForDisplay(d.notes)
                                              ) : (
                                                <span className="fst-italic text-muted">No notes</span>
                                              )}
                                            </span>
                                          </div>
                                          <div className="d-flex flex-column align-items-stretch align-items-md-end gap-1 flex-shrink-0 ms-md-auto text-end small">
                                            {d.promotionAdministrationLocation ? (
                                              <div className="text-muted">
                                                <span className="fw-semibold text-body">Location:</span>{' '}
                                                {String(d.promotionAdministrationLocation)}
                                              </div>
                                            ) : null}
                                            {doseHasPromotionEvidence(d) ? (
                                              <Button
                                                size="sm"
                                                variant="outline-primary"
                                                className="text-nowrap"
                                                onClick={() => openVaccinationCardPromotionEvidence(d.promotionEvidencePath)}
                                              >
                                                View proof
                                              </Button>
                                            ) : null}
                                          </div>
                                          <div className="d-flex flex-shrink-0">
                                            <Button
                                              size="sm"
                                              variant="light-lite"
                                              onClick={() => openEditDose(v.id, idx)}
                                            >
                                              <i className="bi bi-pencil" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="light-lite"
                                              onClick={() => removeDose(v.id, idx)}
                                            >
                                              <i className="bi bi-trash-fill" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>

          <div className="mt-3 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex flex-column">
              <h6 className="fw-semibold mb-0">Vaccine evidence files</h6>
              <p className="small text-muted mb-0">Upload one or more files, then select which vaccines each file is evidence for.</p>
              </div>
              <Button size="sm" variant="outline-primary" onClick={addVaccineEvidenceEntry}>
                Add file mapping
              </Button>
            </div>

            {vaccineEvidenceEntries.length === 0 ? (
              <div className="small text-muted fst-italic">No vaccine evidence files added.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {vaccineEvidenceEntries.map((entry, idx) => (
                  <div key={entry.id} className="border rounded p-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="small fw-semibold">Evidence file #{idx + 1}</div>
                      <Button
                        size="sm"
                        variant="light-lite"
                        onClick={() => removeVaccineEvidenceEntry(entry.id)}
                        title="Remove file mapping"
                      >
                        <i className="bi bi-trash-fill" />
                      </Button>
                    </div>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="file"
                        size="sm"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                          updateVaccineEvidenceEntry(entry.id, {
                            file: f,
                            originalFileName: f ? f.name : entry.originalFileName
                          });
                        }}
                      />
                      <div className="small text-muted mt-1">
                        {entry.file?.name || entry.originalFileName || 'No file selected'}
                      </div>
                    </Form.Group>
                    <div className="small text-muted mb-1">Vaccines covered by this file:</div>
                    <div className="d-flex flex-wrap gap-2">
                      {VACCINES.map((vax) => {
                        const checked = entry.vaccineIds.includes(vax.id);
                        return (
                          <Form.Check
                            key={`${entry.id}-${vax.id}`}
                            type="checkbox"
                            id={`file-vax-${entry.id}-${vax.id}`}
                            label={vax.name}
                            checked={checked}
                            onChange={(e) => {
                              const cur = entry.vaccineIds || [];
                              const next = e.target.checked
                                ? Array.from(new Set([...cur, vax.id]))
                                : cur.filter((id) => id !== vax.id);
                              updateVaccineEvidenceEntry(entry.id, { vaccineIds: next });
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 p-3 border rounded">
            <h6 className="fw-semibold mb-0">TB screening</h6>
            <p className="small text-muted mb-3">Complete TB history and screening documentation.</p>

            <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
              <Form.Label className=" fw-semibold mb-0">History of TB?</Form.Label>
              <Form.Select
                value={tbScreening.diagnosedTb || ''}
                size="sm"
                className="w-auto"
                onChange={(e) => updateTbField('diagnosedTb', e.target.value)}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Select>
            </Form.Group>

            {tbScreening.diagnosedTb === 'yes' ? (
              <>
                <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                  <Form.Label className=" fw-semibold mb-0">Did you receive treatment?</Form.Label>
                  <Form.Select
                    value={tbScreening.treatedTb || ''}
                    size="sm"
                    className='w-auto'
                    onChange={(e) => updateTbField('treatedTb', e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
                {tbScreening.treatedTb === 'yes' ? (
                  <>
                    <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                      <Form.Label className=" fw-semibold mb-0">Treatment regimen</Form.Label>
                      <Form.Select
                        value={tbScreening.treatmentRegimen || ''}
                        size="sm"
                        className='w-auto'
                        onChange={(e) => updateTbField('treatmentRegimen', e.target.value)}
                      >
                        <option value="">Select</option>
                        {TB_TREATMENT_REGIMEN_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    {tbScreening.treatmentRegimen === 'other' ? (
                      <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                        <Form.Label className=" fw-semibold mb-0">Other regimen details</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          className='w-50'
                          value={tbScreening.treatmentRegimenOther || ''}
                          onChange={(e) => updateTbField('treatmentRegimenOther', e.target.value)}
                        />
                      </Form.Group>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}

            <Form.Group className="mb-3 d-flex flex-row align-items-center justify-content-between gap-2">
              <Form.Label className="mb-0 d-flex flex-column">
                <span className="fw-semibold">Have you received the BCG vaccine?</span> 
                <span className="text-muted small fw-normal">(Bacille Calmette-Guerin; not part of routine U.S. vaccination schedule)</span>
              </Form.Label>
              <Form.Select
                value={tbScreening.bcgReceived || ''}
                size="sm"
                className='w-auto'
                onChange={(e) => updateTbField('bcgReceived', e.target.value)}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Select>
            </Form.Group>

            {tbScreening.bcgReceived === 'yes' ? (
              <>
                <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                  <Form.Label className=" fw-semibold mb-0">Date received (BCG)</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    className="w-auto"
                    value={tbScreening.bcgReceivedDate || ''}
                    onChange={(e) => updateTbField('bcgReceivedDate', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                  <Form.Label className=" fw-semibold mb-0"> Have you had a TB Blood Test (IGRA) done?</Form.Label>
                  <Form.Select
                    value={tbScreening.igraDone || ''}
                    size="sm"
                    className='w-auto'
                    onChange={(e) => updateTbField('igraDone', e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
                {tbScreening.igraDone === 'yes' ? (
                  <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                    <Form.Label className=" fw-semibold mb-0">IGRA result</Form.Label>
                    <Form.Select
                      value={tbScreening.igraResult || ''}
                      size="sm"
                      className='w-auto'
                      onChange={(e) => updateTbField('igraResult', e.target.value)}
                    >
                      <option value="">Select</option>
                      {TB_IGRA_RESULT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ) : null}
              </>
            ) : null}

            <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
              <Form.Label className=" fw-semibold mb-0">Have you had a PPD skin test?</Form.Label>
              <Form.Select
                value={tbScreening.ppdDone || ''}
                size="sm"
                className='w-auto'
                onChange={(e) => updateTbField('ppdDone', e.target.value)}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Select>
            </Form.Group>
            {tbScreening.ppdDone === 'yes' ? (
              <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
                <Form.Label className=" fw-semibold mb-0">Choose PPD result</Form.Label>
                <Form.Select
                  value={tbScreening.ppdResult || ''}
                  size="sm"
                  className='w-auto'
                  onChange={(e) => updateTbField('ppdResult', e.target.value)}
                >
                  <option value="">Select</option>
                  {TB_PPD_RESULT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'gt5_high_risk'
                        ? '> 5 mm for high-risk individuals'
                        : opt === 'gt10_moderate_risk'
                          ? '> 10 mm for moderate-risk'
                          : '> 15 mm for low-risk individuals'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}

            <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2">
              <Form.Label className=" fw-semibold mb-0">Chest X-ray completed?</Form.Label>
              <Form.Select
                value={tbScreening.chestXrayDone || ''}
                size="sm"
                className='w-auto'
                onChange={(e) => updateTbField('chestXrayDone', e.target.value)}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Select>
            </Form.Group>
            {tbScreening.chestXrayDone === 'yes' ? (
              <Form.Group>
                <Form.Label className="small text-muted">Upload chest X-ray result (optional)</Form.Label>
                <Form.Control
                  type="file"
                  size="sm"
                  onChange={(e) => setTbXrayFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                />
                <div className="small text-muted mt-1">
                  {tbXrayFile?.name || tbScreening.chestXrayEvidenceOriginalFileName || 'No file selected'}
                </div>
              </Form.Group>
            ) : null}
          </div>

          <Alert variant="info" className="mt-3 mb-0 small">
            Add dose records as needed, then submit for director review. Dose counts are informational only.
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
            <FaSave className="me-1" />
            {submitting ? 'Submitting…' : 'Submit for review'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={doseModal.open}
        onHide={() => setDoseModal({ open: false, vaccineId: null, mode: 'add', doseIndex: null })}
        centered
        enforceFocus={false}
        style={{ zIndex: 1065 }}
      >
        <Modal.Header closeButton className="border-bottom-0">
          <Modal.Title className="d-flex align-items-center gap-2">
            <span className="d-inline-flex align-items-center justify-content-center rounded-2 bg-primary text-white p-2">
              <FaPlus />
            </span>
            <div>
              <div className="fw-bold">{doseModal.mode === 'edit' ? 'Edit dose record' : 'Add dose record'}</div>
              <div className="small text-primary">
                {VACCINES.find((x) => x.id === doseModal.vaccineId)?.name || ''}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Vaccination date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={doseForm.date}
              onChange={(e) => setDoseForm((f) => ({ ...f, date: e.target.value }))}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Add any notes about this dose…"
              value={doseForm.notes}
              onChange={(e) => setDoseForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Form.Group>
          <Alert variant="light" className="border small mb-0">
            <i className="bi bi-info-circle me-2 text-primary" />
            Enter the date you received this dose. Notes are optional.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              setDoseModal({ open: false, vaccineId: null, mode: 'add', doseIndex: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={saveDose}
            disabled={!doseForm.date}
          >
            <FaSave className="me-1" />
            {doseModal.mode === 'edit' ? 'Save changes' : 'Save dose'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
